import { Box, CircularProgress, Link as MuiLink, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';


import { AddNotePanel } from './panels/AddNotePanel';
import { AttachmentsPanel } from './panels/AttachmentsPanel';
import { AttendanceSummaryPanel } from './panels/AttendanceSummaryPanel';
import { BillingPanel } from './panels/BillingPanel';
import { EquipmentFaultPanel } from './panels/EquipmentFaultPanel';
import { HistoryNotesPanel } from './panels/HistoryNotesPanel';
import { InteractionPanel } from './panels/InteractionPanel';
import { AllocationDialog } from './panels/InteractionPanel/AllocationDialog';
import { PartActionDialog } from './panels/InteractionPanel/PartActionDialog';
import { StatusChangeDialog } from './panels/InteractionPanel/StatusChangeDialog';
import { TaskDialog } from './panels/InteractionPanel/TaskDialog';
import { InvoicesPanel } from './panels/InvoicesPanel';
import { JobDetailsPanel } from './panels/JobDetailsPanel';
import { LocationPanel } from './panels/LocationPanel';
import { PartsShipmentsPanel } from './panels/PartsShipmentsPanel';
import { RelatedJobsPanel } from './panels/RelatedJobsPanel';
import { ReviewCompliancePanel } from './panels/ReviewCompliancePanel';
import { SLADetailsPanel } from './panels/SLADetailsPanel';
import { useServiceDetailData } from './useServiceDetailData';
import { useServiceDialogs } from './useServiceDialogs';
import { ActionPermissions } from '../../../@types';
import { PageContainer } from '../../../components/atoms/PageContainer';
import { SummaryBar } from '../../../components/molecules/SummaryBar';
import { PLATFORM_NAME } from '../../../configs/common';
import { isUserHasPermission } from '../../../configs/permissions';
import { TabHandler } from '../../../handlers/TabHandler';
import { useGetUserSystemPermissions } from '../../../queries/user-query';

type TabKey = 'details' | 'administration' | 'learn';

const tabSx = (active: boolean) => ({
  px: 2, py: 1, fontSize: '13px', fontWeight: active ? 600 : 500,
  color: active ? 'v4.accent' : 'text.secondary',
  borderBottom: active ? '2px solid' : '2px solid transparent',
  borderBottomColor: active ? 'v4.accent' : 'transparent',
  cursor: 'pointer', background: 'none', border: 'none', fontFamily: 'inherit', mb: '-2px',
});

export const UpdateServiceV4: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const serviceId = id ? parseInt(id, 10) : 0;
  const [activeTab, setActiveTab] = useState<TabKey>('details');

  TabHandler(`Job ${id} | ${PLATFORM_NAME}`);

  const permissions = useGetUserSystemPermissions().data;
  const {
    service, serviceQuery, logs, clientLogoPic,
    nodeAttrsQuery, tradingHoursQuery, coverageHoursQuery,
    attendanceData, attendanceMetrics, clientContractIds,
    billing, billingInfoData, internalInvoices, externalInvoices,
    otherJobsAtLocation, closedJobsAtLocation, linkedJobs,
    handleFieldUpdate, handleSaveAddress, handleAddNote, handleSaveBillingInfo, handleRefresh,
  } = useServiceDetailData(serviceId);

  const statusCode = service?.serviceStatus?.code ?? '';
  const serviceTypeCode = service?.serviceTypeCode ?? '';
  const dialogs = useServiceDialogs(serviceId, statusCode, serviceTypeCode, handleRefresh);
  const hasAdminPermission = isUserHasPermission(ActionPermissions.Service_Edit_Administration_Section, permissions);

  if (serviceQuery.isLoading) {
    return (
      <PageContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  if (serviceQuery.isError || (!serviceQuery.isLoading && !service)) {
    return (
      <PageContainer>
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="error">Service not found</Typography>
          <MuiLink component={RouterLink} to="/services" underline="hover" sx={{ color: 'v4.accent' }}>Back to Jobs</MuiLink>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
        <Box sx={{ px: 2.5, py: 1.25, fontSize: '13px', color: 'text.secondary', display: 'flex', gap: 0.75 }}>
          <MuiLink component={RouterLink} to="/services" underline="none" sx={{ color: 'v4.accent' }}>Jobs</MuiLink>
          <span>/</span>
          <span>JOB-{id}</span>
        </Box>

        <Box sx={{ position: 'sticky', top: 0, zIndex: 50, bgcolor: 'background.default', px: 2.5, py: 1, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <SummaryBar service={service} logoPic={clientLogoPic} onStatusChange={dialogs.handleOpenStatusDialog} onAllocate={dialogs.handleOpenAllocationDialog} onPartAction={dialogs.handleOpenPartActionDialog} onTask={dialogs.handleOpenTaskDialog} />
        </Box>

        {service && <InteractionPanel service={service} serviceId={serviceId} permissions={permissions} onComplete={handleRefresh} />}

        <StatusChangeDialog open={dialogs.statusDialogOpen} onClose={dialogs.handleCloseStatusDialog} currentStatus={service?.serviceStatus?.name ?? ''} possibleStatuses={dialogs.nextStatusesQuery.data?.possibleNextStatuses ?? []} preferredStatuses={dialogs.nextStatusesQuery.data?.preferedStatuses ?? []} loading={dialogs.nextStatusesQuery.isFetching} onSubmit={dialogs.handleStatusSubmit} submitting={dialogs.statusSubmitting} />
        <AllocationDialog open={dialogs.allocationDialogOpen} onClose={dialogs.handleCloseAllocationDialog} currentTechnician={service?.primaryAllocation?.node?.name ?? ''} onSubmit={dialogs.handleAllocationSubmit} submitting={dialogs.allocationSubmitting} />
        <PartActionDialog open={dialogs.partActionDialogOpen} onClose={dialogs.handleClosePartActionDialog} serviceId={serviceId} clientId={service?.clientId ?? 0} contractId={service?.contractId ?? 0} clientContractIds={[service?.clientId ?? 0, service?.contractId ?? 0]} locationId={service?.mainLocationNodeId ?? 0} canCreateExceptional={isUserHasPermission(ActionPermissions.Service_Edit_Part_Actions_Exceptional_Creation, permissions)} onSubmit={dialogs.handlePartActionSubmit} submitting={dialogs.partActionSubmitting} />
        <TaskDialog open={dialogs.taskDialogOpen} onClose={dialogs.handleCloseTaskDialog} serviceId={serviceId} taskTypes={dialogs.taskTypesQuery.data ?? []} taskTypesLoading={dialogs.taskTypesQuery.isFetching} onSubmit={dialogs.handleTaskSubmit} submitting={dialogs.taskSubmitting} />

        <Box sx={{ mx: 2.5 }}>
          <Box role="tablist" aria-label="Service detail tabs" sx={{ display: 'flex', gap: 0, borderBottom: '2px solid', borderColor: 'v4.borderDark' }}>
            <Box component="button" role="tab" aria-selected={activeTab === 'details'} sx={tabSx(activeTab === 'details')} onClick={() => setActiveTab('details')}>Details</Box>
            {hasAdminPermission && <Box component="button" role="tab" aria-selected={activeTab === 'administration'} sx={tabSx(activeTab === 'administration')} onClick={() => setActiveTab('administration')}>Administration</Box>}
            <Box component="button" role="tab" aria-selected={activeTab === 'learn'} sx={tabSx(activeTab === 'learn')} onClick={() => setActiveTab('learn')}>Other</Box>
          </Box>
        </Box>

        {activeTab === 'details' && (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 3fr', gap: 1.75, px: 2.5, pt: 1.75, alignItems: 'start' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                <JobDetailsPanel service={service} onFieldUpdate={handleFieldUpdate} />
                <EquipmentFaultPanel service={service} />
                <AddNotePanel onAddNote={handleAddNote} />
                <AttachmentsPanel serviceId={serviceId} />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                <LocationPanel service={service} nodeAttributes={nodeAttrsQuery.data as unknown as Array<Record<string, unknown>>} tradingHoursData={tradingHoursQuery.data as unknown as Array<Record<string, unknown>>} coverageHoursData={coverageHoursQuery.data as unknown as Array<Record<string, unknown>>} onSaveAddress={handleSaveAddress} />
                <PartsShipmentsPanel serviceId={serviceId} sourceNodeId={service?.mainLocationNodeId ?? 0} sourceName={service?.primaryAllocation?.node?.name ?? service?.mainLocation ?? ''} clientHierarchy={service?.clientHierarchy} clientContractIds={clientContractIds} clientName={service?.client ?? ''} contractName={service?.contract ?? ''} onRefresh={handleRefresh} />
                <RelatedJobsPanel currentServiceId={serviceId} otherJobsAtLocation={otherJobsAtLocation} linkedJobs={linkedJobs} />
              </Box>
            </Box>
            <Box sx={{ px: 2.5, py: 1.75 }}>
              <HistoryNotesPanel logs={logs} serviceId={serviceId} permissions={permissions} />
            </Box>
          </>
        )}

        {activeTab === 'administration' && hasAdminPermission && (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.75, px: 2.5, pt: 1.75, alignItems: 'start' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
              <ReviewCompliancePanel billingInfo={billingInfoData} onSave={handleSaveBillingInfo} />
              <BillingPanel billing={billing} />
              <InvoicesPanel internalInvoices={internalInvoices} externalInvoices={externalInvoices} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
              <AttendanceSummaryPanel attendance={attendanceData as unknown as Array<Record<string, unknown>>} metrics={attendanceMetrics} />
              <SLADetailsPanel serviceId={serviceId} />
            </Box>
          </Box>
        )}

        {activeTab === 'learn' && (
          <Box sx={{ px: 2.5, pt: 1.75 }}>
            <RelatedJobsPanel title="Closed Jobs at This Location" currentServiceId={serviceId} otherJobsAtLocation={closedJobsAtLocation} />
          </Box>
        )}
      </Box>
    </PageContainer>
  );
};
